import { SITE } from './site';

const E = SITE.email;

export const termsDoc = {
  title: 'Terms of Service',
  description:
    'The rules for using DashLink Gaming: eligibility, accounts, acceptable use, user content, third-party content, disclaimers, liability and governing law.',
  intro: `These Terms of Service (“Terms”) are an agreement between you and ${SITE.operator} (“we”, “us”) for your use of ${SITE.domain} and the DashLink Gaming web app (the “Service”). By creating an account or using the Service you agree to these Terms, our Privacy Policy and our Community Guidelines. If you do not agree, please do not use the Service.`,
  sections: [
    {
      id: 'eligibility',
      heading: '1. Who can use DashLink Gaming',
      body: [
        'You must be at least 13 years old to use the Service. If you are under 18, or under the age of majority or digital consent where you live, you confirm that a parent or legal guardian has reviewed and agreed to these Terms on your behalf. You may not use the Service if you have previously been banned or if the law where you live prohibits it.',
      ],
    },
    {
      id: 'accounts',
      heading: '2. Your account',
      body: [
        {
          list: [
            'Give us accurate information, including a working email address, and keep it up to date. Some features, such as multiplayer rooms, require a verified email.',
            'Keep your password secret. You are responsible for activity on your account. Tell us immediately at ' + E + ' if you think your account has been compromised.',
            'One person per account. Do not sell, share or transfer your account, and do not impersonate anyone.',
            'Usernames must follow our Community Guidelines. We may change or reclaim usernames that are offensive, misleading or infringe someone else’s rights.',
          ],
        },
      ],
    },
    {
      id: 'service',
      heading: '3. The Service',
      body: [
        'DashLink Gaming is a free social gaming hub. It includes built-in multiplayer games (currently Tic-Tac-Toe and Checkers), game and room chat with emojis and voice dictation, leaderboards, squad matching, squad invites, friends and watch parties. It also lists information about other video games sourced from third-party databases such as RAWG.io and FreeToGame; listing a game does not mean that game is playable on DashLink or that we are affiliated with its publisher.',
        'We are constantly improving the Service and may add, change or remove features. Scores, ranks and stats have no monetary value and cannot be exchanged for money or prizes. We do not guarantee the Service will always be available or error-free.',
      ],
    },
    {
      id: 'user-content',
      heading: '4. Your content',
      body: [
        'You keep ownership of the messages, profile information and links you post (“User Content”). You grant us a worldwide, non-exclusive, royalty-free licence to host, store, display and transmit your User Content only as needed to operate and improve the Service. This licence ends when you delete the content or your account, except for copies other users have already seen or that we must keep by law.',
        'You are responsible for your User Content and confirm you have the right to post it. Do not post anything illegal, harmful or that infringes someone else’s rights.',
      ],
    },
    {
      id: 'acceptable-use',
      heading: '5. Acceptable use',
      body: [
        'You agree not to:',
        {
          list: [
            'harass, threaten, bully or abuse others, or post hate speech, sexual content involving minors, or content promoting violence or self-harm;',
            'cheat, exploit bugs, use bots or automation, or manipulate leaderboards or game outcomes;',
            'spam, run scams, phish, or advertise without permission;',
            'share other people’s personal information without consent;',
            'share links to pirated streams or content you are not allowed to share in watch parties;',
            'attempt to hack, overload, reverse-engineer or disrupt the Service, or access accounts or data that are not yours;',
            'use the Service in breach of any applicable law.',
          ],
        },
        'Full details are in our Community Guidelines at /community-guidelines.',
      ],
    },
    {
      id: 'third-party',
      heading: '6. Third-party content, links and embeds',
      body: [
        'The Service shows content from third parties, including game data and images provided by RAWG.io, free-to-play game listings from FreeToGame, and live streams embedded from Twitch and YouTube. These are provided by their owners under their own terms and privacy policies. We do not control and are not responsible for third-party content, websites or services, and links to them are not endorsements.',
        'All game titles, logos and artwork are trademarks or copyrights of their respective owners and are used for identification and informational purposes only.',
      ],
    },
    {
      id: 'ads',
      heading: '7. Advertising',
      body: [
        'The Service may display advertising, including ads served by Google AdSense. Advertisers are responsible for their ads. Clicking an ad may take you to a third-party site governed by its own terms. You must not click ads artificially or encourage others to do so.',
      ],
    },
    {
      id: 'ip',
      heading: '8. Our intellectual property',
      body: [
        'The Service, including its design, software, text, graphics and the DashLink Gaming name and logo, is owned by us or our licensors and is protected by intellectual property laws. We grant you a limited, personal, non-exclusive, non-transferable, revocable licence to use the Service for your own non-commercial entertainment in line with these Terms.',
        'If you believe content on the Service infringes your copyright or other rights, email ' + E + ' with details of the work, the location of the content and your contact information, and we will respond promptly (including under the US DMCA, the EU Digital Services Act and India’s IT Rules, 2021, as applicable).',
      ],
    },
    {
      id: 'moderation',
      heading: '9. Moderation, suspension and termination',
      body: [
        'We may remove content, restrict features, or suspend or close accounts that break these Terms or our Community Guidelines, or where required by law. Where reasonable we will tell you why and give you a chance to respond by emailing ' + E + '. You may stop using the Service and ask us to delete your account at any time.',
      ],
    },
    {
      id: 'disclaimers',
      heading: '10. Disclaimers',
      body: [
        'The Service is provided free of charge on an “as is” and “as available” basis. To the maximum extent permitted by law, we disclaim all warranties, express or implied, including merchantability, fitness for a particular purpose and non-infringement. We do not verify the identity of users or the accuracy of User Content or third-party game data.',
        'Nothing in these Terms limits any rights you have as a consumer that cannot be excluded under the law of your country of residence.',
      ],
    },
    {
      id: 'liability',
      heading: '11. Limitation of liability',
      body: [
        'To the maximum extent permitted by law, we are not liable for any indirect, incidental, special, consequential or punitive damages, or for loss of data, profits, goodwill or game progress, arising from your use of the Service. Our total liability for any claim relating to the Service is limited to the greater of the amount you paid us in the 12 months before the claim (if any) or USD 50.',
        'These limits do not apply to liability for death or personal injury caused by our negligence, fraud, or any other liability that cannot be limited under applicable law, including consumer protection laws in the EU, UK, India and the UAE.',
      ],
    },
    {
      id: 'indemnity',
      heading: '12. Indemnity',
      body: [
        'To the extent permitted by law, you agree to indemnify us against claims, losses and costs arising from your User Content or your breach of these Terms. This section does not apply to consumers in jurisdictions where such indemnities are not enforceable.',
      ],
    },
    {
      id: 'law',
      heading: '13. Governing law and disputes',
      body: [
        'Please contact us first at ' + E + ' — most concerns can be resolved quickly and informally.',
        'These Terms are governed by the laws of India, and the courts of India have jurisdiction, without prejudice to the following: if you live in the European Union, EEA or United Kingdom you also benefit from the mandatory consumer protection laws of your country of residence and may bring proceedings in your local courts; if you live in the United Arab Emirates, nothing in these Terms limits your rights under UAE consumer protection law; and if you live in the United States, nothing in these Terms limits any non-waivable rights under the laws of your state. EU residents may also use the European Commission’s online dispute resolution information at https://consumer-redress.ec.europa.eu.',
      ],
    },
    {
      id: 'changes',
      heading: '14. Changes to these Terms',
      body: [
        'We may update these Terms from time to time. We will update the “Last updated” date and, for material changes, notify signed-in users in the app or by email at least 14 days before they take effect, unless a change is required sooner by law or to address abuse. If you continue to use the Service after the changes take effect, you accept the updated Terms.',
      ],
    },
    {
      id: 'misc',
      heading: '15. General',
      body: [
        'If any part of these Terms is found unenforceable, the rest remains in effect. Our failure to enforce a provision is not a waiver. You may not transfer your rights under these Terms; we may transfer ours as part of a reorganisation, merger or sale, with notice to you. These Terms, together with the Privacy Policy, Cookie Policy and Community Guidelines, are the entire agreement between you and us about the Service.',
      ],
    },
    {
      id: 'contact',
      heading: '16. Contact',
      body: [`${SITE.operator} — ${E}`],
    },
  ],
};
